import type { Module } from '@/types/course';

export const module6: Module = {
  slug: 'modulo-6-vender-oro',
  number: 6,
  title: 'Vender tu oro: liquidez y realización',
  description:
    'Toda inversión termina algún día. Vender oro requiere conocimiento específico para no perder valor en el último paso. Este módulo te prepara para realizar tu inversión sin errores.',
  durationMinutes: 50,
  badge: 'vendedor-inteligente',
  lessons: [
    {
      slug: 'como-donde-vender-oro',
      number: '6.1',
      title: 'Cómo y dónde vender oro de inversión',
      durationMinutes: 17,
      videoUrl: null,
      content: `Vender oro físico de inversión es relativamente simple si sabes a quién acudir y cómo prepararte.

**Canales de venta principales**:

**1. Distribuidor donde compraste** (opción preferida). La mayoría de distribuidores profesionales tienen política de recompra clara: te pagan el spot menos un descuento (0,5-2% para lingotes 100g+; 1-5% para piezas pequeñas). Ventajas: confianza ya establecida, factura original disponible, proceso ágil. Oro Express, por ejemplo, recompra el oro que vende a sus clientes con condiciones predefinidas.

**2. Otros distribuidores profesionales**. Si tu distribuidor original ya no opera o ofrece peor precio, puedes vender a otros profesionales. Llevarás tu factura de compra original y tus piezas en blister. Te harán verificación (peso, autenticación) y harán oferta. Compara al menos 2-3 ofertas.

**3. Bancos**. Algunos compran oro pero con descuentos significativos sobre el spot (3-8%). Adecuado solo si ya operas con esa entidad y priorizas trato bancario.

**4. Casas de empeño y compra-venta locales**. Compran al peso. Útiles para joyería usada o piezas no certificadas, pero **suelen ofrecer precios muy inferiores al mercado profesional** (5-15% bajo spot). Evitar para oro de inversión legítimo.

**5. Particulares**. Plataformas tipo Wallapop, foros especializados, redes sociales. Riesgos: estafas, falsificación inversa (te pagan con billetes falsos), cuestiones fiscales sin factura. **Solo recomendable con verificación rigurosa**.

**6. Subastas profesionales**. Para piezas numismáticas raras, casas como Sotheby's, Christie's o Cayón en España. No para oro bullion estándar.

**Preparación antes de vender**:

1. **Reúne las facturas originales** de compra. Sin ellas, ni puedes acreditar precio de adquisición fiscal ni demuestras procedencia legítima.
2. **Mantén las piezas en blister cuando aplique**. Romper el blister reduce el precio.
3. **Conoce el spot del día**. Consulta varias fuentes antes de aceptar precio.
4. **Pide oferta por escrito**. Distribuidores profesionales te dan precio cerrado con timing concreto (válido X horas).
5. **Compara ofertas**: dos o tres distribuidores profesionales pueden mostrar diferencias del 1-3%.

**Documentación legal**:

- **Factura del distribuidor recomprador**: específica las piezas, peso, pureza, precio y método de pago.
- **Identificación**: te pedirán DNI obligatoriamente (AML).
- **Pago**: por transferencia bancaria si supera 1.000€. Conserva el justificante.

**Tiempo de la operación**:

- En distribuidor profesional: 30-60 minutos en oficina, transferencia mismo día o siguiente.
- Online (envío seguro): 3-7 días desde envío hasta cobro.
- En banco: 1-3 días.

> *Este contenido es educativo y orientativo.*`,
      keyPoints: [
        'Mejor canal: el distribuidor donde compraste (confianza + factura original + proceso ágil).',
        'Comparar 2-3 ofertas profesionales puede aportar 1-3% adicional.',
        'Bancos compran con descuentos altos; casas de empeño suelen ofrecer precios bajos.',
        'Conserva facturas y blisters: son críticos para precio y fiscalidad.',
        'Pago obligatorio por transferencia bancaria si supera 1.000€.',
      ],
    },
    {
      slug: 'tasacion-evitar-estafas',
      number: '6.2',
      title: 'Tasación: cómo evitar ser estafado',
      durationMinutes: 17,
      videoUrl: null,
      content: `Vender oro implica que alguien evalúa tus piezas y te ofrece un precio. Conocer el proceso de tasación profesional es tu mejor defensa frente a manipulación o estafa.

**Qué incluye una tasación profesional honesta**:

1. **Verificación de autenticidad**:
   - Visual: marcas de refinería, números de serie, sellos.
   - Peso preciso: balanza calibrada con precisión de 0,01 g.
   - Densidad: el oro tiene densidad de 19,32 g/cm³. Cualquier desviación importante indica falsificación o aleación.
   - Conductividad eléctrica o ultrasónica (en piezas grandes o sin certificado).
   - Análisis XRF (fluorescencia de rayos X) para verificar pureza sin dañar la pieza.

2. **Cálculo del precio**:
   - Spot price del momento.
   - Aplicación de descuento estándar según formato.
   - Bonificación por estado (blister sellado, certificado original).

3. **Oferta transparente**:
   - Por escrito.
   - Con desglose: spot del momento, descuento aplicado, precio final.
   - Validez temporal definida (X horas).

**Banderas rojas en una tasación**:

- ❌ **No verifican delante de ti**. Llevarse las piezas a "trastienda" sin transparencia.
- ❌ **No muestran spot del día**. Manipular el precio de referencia.
- ❌ **Aplican descuentos anómalos** (más del 5% en lingotes grandes con factura).
- ❌ **Presión de tiempo**: "este precio es solo si vendes ahora".
- ❌ **Cuestionan autenticidad sin razón clara** para presionar a la baja.
- ❌ **Pago en efectivo grande**: puede ser estafa con billetes falsos o problemas legales (límite 1.000€).
- ❌ **No emiten factura completa** o se resisten a documentar la operación.

**Errores que reducen el precio (sin ser estafa)**:

- **Romper blister**: 1-5% menos.
- **Marcas, golpes, oxidación leve** en piezas: 1-3% menos.
- **No tener certificado/factura**: 2-5% menos (especialmente en lingotes pequeños).
- **Vender en mal momento del mercado**: las primas físicas se reducen tras subidas verticales (los distribuidores tienen mucho stock y descuentan más).

**Procesos más comunes de estafa**:

1. **Sustitución de pieza**: el comprador "examina" tu lingote y te devuelve uno parecido pero tungsteno chapado. Solución: no sueltes la pieza de vista.

2. **Manipulación de balanza**: balanza ligeramente descalibrada que muestra menos peso. Solución: pide pesar en otra balanza o llevar la tuya certificada.

3. **Spot manipulado**: te muestran un spot inferior al real para reducir oferta. Solución: lleva el spot consultado en fuente independiente (kitco, goldprice).

4. **Pruebas falsas de autenticidad**: cuestionan tu pieza para presionar a la baja con la promesa de "te lo compro pero más barato". Solución: pide segunda opinión de otro tasador.

5. **Estafa de pago**: te pagan con cheque que rebota o billetes falsos. Solución: solo aceptar transferencia bancaria con justificante.

**Cómo elegir tasador de confianza**:

- Distribuidor profesional con historial verificable.
- Asociación profesional del sector.
- Recomendaciones de otros inversores.
- Disposición a hacer la tasación delante de ti, paso a paso.
- Precio competitivo pero realista (precios extraordinariamente altos también son sospechosos).

> *Este contenido es educativo y orientativo. Ante cualquier duda, busca segunda opinión profesional.*`,
      keyPoints: [
        'Tasación honesta incluye verificación delante de ti, peso preciso, densidad, conductividad y oferta escrita.',
        'Banderas rojas: trastienda, presión de tiempo, descuentos anómalos, pago en efectivo grande, no emitir factura.',
        'Sustitución de pieza, balanza manipulada y spot falso son las estafas más comunes: protégete con vigilancia constante.',
        'Cobra siempre por transferencia bancaria con justificante; nunca aceptes cheques o efectivo grande.',
        'Compara ofertas de 2-3 tasadores antes de cerrar operaciones grandes.',
      ],
    },
    {
      slug: 'optimizacion-fiscal-venta',
      number: '6.3',
      title: 'Optimización fiscal en la venta',
      durationMinutes: 16,
      videoUrl: null,
      content: `Vender con cabeza fiscal puede suponer ahorros relevantes. Aquí, los principales recursos legales disponibles en España.

**Lo básico: cálculo de la ganancia patrimonial**

\`\`\`
Ganancia patrimonial = Precio venta − (Precio compra + Gastos asociados)
\`\`\`

Gastos deducibles:
- Factura original del distribuidor.
- Comisiones documentadas.
- Costes de tasación si los hubo al comprar.
- Costes de custodia profesional si existen y están documentados.

**Tributación en IRPF (base ahorro)**:
- 19% (primeros 6.000€)
- 21% (6.000-50.000€)
- 23% (50.000-200.000€)
- 27% (200.000-300.000€)
- 28% (>300.000€)

**Estrategias de optimización legal**:

**1. Compensación con pérdidas**

Si tienes pérdidas en otras inversiones (acciones, fondos, criptomonedas), pueden **compensar la ganancia del oro** en el mismo ejercicio. El oro forma parte del mismo "compartimento" fiscal de ganancias y pérdidas patrimoniales.

Ejemplo: vendes oro con +10.000€ ganancia y tienes acciones con −4.000€ pérdida. Tributas solo por 6.000€.

**2. Distribución de ventas en varios ejercicios fiscales**

Si la venta supera tramos altos (>50.000€ ganancia), distribuir en 2-3 ejercicios mantiene la tributación en tramos más bajos.

Ejemplo: ganancia total 100.000€. Vender todo en un año: pasas por tramos del 21-23%. Vender 33.000€/año durante 3 años: parcialmente en 19-21%.

**3. Aprovechar el mínimo exento autonómico**

En IRPF general, no hay mínimo exento sobre ganancias patrimoniales (todas tributan). Pero en Patrimonio sí: si tu patrimonio está cerca del límite autonómico, vender oro puede ayudar a optimizar el conjunto.

**4. Fiscalidad del oro heredado o donado**

- **Heredado**: el valor de adquisición a efectos de futura venta es el declarado en la sucesión, no el de compra original. Esto puede ser muy ventajoso si el oro se compró hace décadas.
- **Donado**: implica Impuesto sobre Donaciones para el receptor, pero la base de cálculo de futura venta también es el valor declarado en la donación.

**5. Reinversión sin penalización fiscal**

A diferencia de la vivienda habitual, el oro **no tiene exención por reinversión**. Vender oro y comprar más oro genera tributación normal sobre la ganancia.

**6. Cambio de residencia fiscal**

Algunas jurisdicciones tienen tributación más favorable sobre ganancias patrimoniales. Cualquier cambio de residencia fiscal debe planificarse con asesoría especializada (anti-elusión, exit tax si aplica).

**Errores fiscales costosos**:

- **No declarar la venta**: las operaciones por transferencia están trazadas. Hacienda puede detectar la operación y aplicar sanciones del 50-150% sobre la cuota no ingresada.
- **No conservar facturas**: implica que Hacienda puede asumir precio de adquisición cero. Tributa sobre el 100% del precio de venta.
- **Ventas en efectivo no documentadas**: además de incumplir el límite de 1.000€, complica acreditar la operación legalmente.
- **Confundir oro de inversión con joyería**: la joyería personal de uso se considera bien afecto a uso personal y tiene reglas fiscales distintas.

**Recomendación práctica**:

Antes de cualquier venta significativa (>20.000€ de ganancia), consulta con un asesor fiscal cualificado. La optimización legal puede suponer ahorros de miles de euros, y los errores pueden multiplicar la tributación.

> **Disclaimer importante**: La fiscalidad cambia con frecuencia y depende de circunstancias personales y CCAA de residencia. Este contenido es educativo a fecha del curso. **Consulta siempre con un asesor fiscal cualificado para tu caso particular.**`,
      keyPoints: [
        'Ganancia = precio venta − (precio compra + gastos documentados); tributación en base ahorro IRPF (19-28%).',
        'Compensar con pérdidas de otras inversiones del mismo ejercicio reduce la base imponible.',
        'Distribuir ventas grandes en varios ejercicios mantiene tributación en tramos más bajos.',
        'El oro heredado se valora en la sucesión: ventaja fiscal si se compró hace décadas.',
        'Errores caros: no declarar, no tener facturas, confundir formatos. Consulta siempre asesor fiscal antes de operaciones grandes.',
      ],
    },
  ],
  quiz: [
    {
      id: 'm6-q1',
      question:
        '¿Cuál es el mejor canal habitualmente para vender oro de inversión?',
      options: [
        'Casa de empeño local',
        'El distribuidor profesional donde lo compraste',
        'Wallapop',
        'Banco que no conoces',
      ],
      correctIndex: 1,
      explanation:
        'El distribuidor original ofrece confianza ya establecida, conoce tu factura, y suele tener política de recompra clara. Es el canal con mejor relación precio/seguridad.',
    },
    {
      id: 'm6-q2',
      question:
        '¿Qué hacer si un tasador quiere examinar tu lingote en "trastienda"?',
      options: [
        'Permitirlo, es procedimiento normal',
        'Insistir en que la verificación se haga delante de ti',
        'Aceptarlo si te dan recibo',
        'Preguntar en otra ocasión',
      ],
      correctIndex: 1,
      explanation:
        'La sustitución de pieza es una estafa documentada. Toda verificación profesional honesta se realiza con la pieza a la vista del cliente.',
    },
    {
      id: 'm6-q3',
      question:
        'Vendes oro con ganancia de 8.000€ y tienes 3.000€ de pérdidas en acciones del mismo año. ¿Cuál es la base imponible final?',
      options: ['8.000€', '11.000€', '5.000€', '0€'],
      correctIndex: 2,
      explanation:
        'Las pérdidas patrimoniales pueden compensar las ganancias del mismo ejercicio. 8.000 − 3.000 = 5.000€ tributables.',
    },
    {
      id: 'm6-q4',
      question:
        '¿Qué documento es CRÍTICO conservar para optimizar la fiscalidad de la venta?',
      options: [
        'Una foto del lingote en casa',
        'La factura original de compra',
        'El recibo de la caja fuerte',
        'El extracto bancario de la transferencia',
      ],
      correctIndex: 1,
      explanation:
        'Sin factura de compra, Hacienda puede asumir precio de adquisición cero, lo que dispararía la tributación sobre el 100% del precio de venta.',
    },
    {
      id: 'm6-q5',
      question:
        '¿Por qué método debes cobrar la venta de oro si supera 1.000€?',
      options: [
        'Efectivo, para mayor discreción',
        'Cheque al portador',
        'Transferencia bancaria con justificante',
        'Bitcoin para no pagar impuestos',
      ],
      correctIndex: 2,
      explanation:
        'La normativa antiblanqueo limita pagos en efectivo a 1.000€. La transferencia bancaria es obligatoria y, además, deja prueba legal de la operación para fiscalidad.',
    },
  ],
};
